import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
// Fix: Import icons correctly - they are components, not objects
import { FaPhone, FaEnvelope } from "react-icons/fa";
import { FiArrowRight } from "react-icons/fi";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'services', label: 'Services', path: '/services' },
    { id: 'portfolio', label: 'Portfolio', path: '/portfolio' },
    { id: 'about', label: 'About', path: '/about' },
    { id: 'privacypolicy', label: 'Privacy Policy', path: '/privacy-policy' },
    { id: 'contact', label: 'Contact', path: '/contact' },
    { id: 'company', label: 'Company', path: '/company-profile' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header 
      className={`header ${scrolled ? 'scrolled' : ''}`} 
      ref={headerRef}
      role="banner"
    >
      {/* Top Contact Bar */}
      <div className="contact-bar">
        <div className="container">
          <div className="contact-info2">
            <div className="contact-item2">
              {/* Fix: Render icon as component */}
              <FaPhone className="contact-icon2" />
              <a href="tel:+919301542085" className="contact-item2">
                +91 9301542085
              </a>
            </div>
            <div className="contact-item2">
              {/* Fix: Render icon as component */}
              <FaEnvelope className="contact-icon2" />
              <a href="mailto:codrivatech.contact@gmail.com" className="contact-item2">
                codrivatech.contact@gmail.com
              </a>
            </div>
          </div>
          <button onClick={() => navigate('/contact')} className="cta-button">
            Get Free Quote
            {/* Fix: Render icon as component */}
            <FiArrowRight className="button-icon" />
          </button>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container">
        <nav className="navbar" role="navigation" aria-label="Main Navigation">
          {/* Logo */}
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img
              src="/companylogo.png"
              alt="Codriva Tech - Professional IT Solutions Company"
              className="logo-img"
              loading="eager"
              width="45"
              height="45"
              onError={(e) => {
                e.target.style.display = 'none';
                // Optional: Show fallback text
              }}
            />
            <div className="logo-text">
              <span className="logo-primary">Codriva Tech</span>
              <span className="logo-tagline">IT Solutions</span>
            </div>
          </div>

          {/* Navigation Links */}
          <div className={`nav-links ${isOpen ? "active" : ""}`}>
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleNavigation(item.path)}
                aria-label={`Navigate to ${item.label}`}
              >
                <span className="link-text">{item.label}</span>
                <span className="link-underline"></span>
              </button>
            ))}
            
            {/* Mobile CTA Button */}
            <div className="mobile-cta">
              <button 
                className="contact-cta"
                onClick={() => handleNavigation('/contact')}
              >
                {/* Fix: Render icon as component */}
                <FaPhone className="cta-icon" />
                <span>Contact Us</span>
              </button>
            </div>
          </div>

          {/* Desktop CTA Button */}
          <button onClick={() => navigate('/contact')} className="desktop-cta">
            <span>Start Project</span>
            {/* Fix: Render icon as component */}
            <FiArrowRight className="cta-icon" />
          </button>

          {/* Mobile Toggle Button */}
          <button 
            className="nav-toggle" 
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            <div className={`hamburger ${isOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </nav>
      </div>

      {/* Keep your existing styles here */}
      <style jsx>{`
        /* Header Base Styles */
        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: transparent;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(0px);
        }

        .header.scrolled {
          background: rgba(2, 6, 23, 0.95);
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
          border-bottom: 1px solid rgba(34, 211, 238, 0.1);
        }

        /* Contact Bar */
        .contact-bar {
          background: linear-gradient(90deg, #22d3ee, #3b82f6);
          padding: 10px 0;
          display: none;
        }

        .header.scrolled .contact-bar {
          display: block;
          animation: slideDown 0.3s ease;
        }

        .contact-bar .container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 32px;
        }

        .contact-info2 {
          display: flex;
          gap: 30px;
        }

        .contact-item2 {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .contact-icon2 {
          font-size: 0.9rem;
          color: #020617;
        }

        .contact-item2 a {
          color: #020617;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
          transition: opacity 0.3s ease;
        }

        .contact-item2 a:hover {
          opacity: 0.8;
        }

        .cta-button {
          background: #020617;
          color: #22d3ee;
          padding: 8px 20px;
          border-radius: 50px;
          border: none;
          font-size: 0.9rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 2px 10px rgba(2, 6, 23, 0.2);
          cursor: pointer;
        }

        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(2, 6, 23, 0.3);
          gap: 12px;
        }

        .button-icon {
          transition: transform 0.3s ease;
        }

        .cta-button:hover .button-icon {
          transform: translateX(3px);
        }

        /* Container */
        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 32px;
        }

        /* Navigation Bar */
        .navbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 0;
          position: relative;
        }

        /* Logo */
        .logo {
          display: flex;
          align-items: center;
          gap: 15px;
          text-decoration: none;
          z-index: 1001;
          cursor: pointer;
        }

        .logo-img {
          width: 45px;
          height: 45px;
          object-fit: contain;
          filter: drop-shadow(0 0 10px rgba(34, 211, 238, 0.4));
          border-radius: 10px;
          transition: transform 0.3s ease;
        }

        .logo:hover .logo-img {
          transform: scale(1.05);
        }

        .logo-text {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }

        .logo-primary {
          font-size: 1.6rem;
          font-weight: 800;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.5px;
        }

        .logo-tagline {
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 500;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        /* Navigation Links */
        .nav-links {
          display: flex;
          align-items: center;
          gap: 40px;
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .nav-link {
          background: none;
          border: none;
          position: relative;
          padding: 10px 0;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          color: #cbd5e1;
          transition: color 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .nav-link:hover {
          color: #f8fafc;
        }

        .nav-link.active {
          color: #22d3ee;
        }

        .link-text {
          position: relative;
          z-index: 2;
        }

        .link-underline {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #22d3ee, #3b82f6);
          border-radius: 1px;
          transition: width 0.3s ease, opacity 0.3s ease;
          opacity: 0;
        }

        .nav-link:hover .link-underline,
        .nav-link.active .link-underline {
          width: 100%;
          opacity: 1;
        }

        /* Desktop CTA Button */
        .desktop-cta {
          padding: 12px 28px;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          color: #020617;
          border-radius: 12px;
          border: none;
          font-weight: 600;
          font-size: 0.95rem;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(34, 211, 238, 0.3);
          z-index: 1001;
          cursor: pointer;
        }

        .desktop-cta:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 25px rgba(34, 211, 238, 0.4);
          gap: 14px;
        }

        .desktop-cta:hover .cta-icon {
          transform: translateX(3px);
        }

        .cta-icon {
          transition: transform 0.3s ease;
        }

        /* Mobile CTA (hidden on desktop) */
        .mobile-cta {
          display: none;
        }

        /* Mobile Toggle Button */
        .nav-toggle {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          width: 40px;
          height: 40px;
          position: relative;
          z-index: 1001;
        }

        .hamburger {
          width: 24px;
          height: 18px;
          position: relative;
          transform: rotate(0deg);
          transition: .5s ease-in-out;
          cursor: pointer;
          margin: 0 auto;
        }

        .hamburger span {
          display: block;
          position: absolute;
          height: 2px;
          width: 100%;
          background: #22d3ee;
          border-radius: 9px;
          opacity: 1;
          left: 0;
          transform: rotate(0deg);
          transition: .25s ease-in-out;
        }

        .hamburger span:nth-child(1) {
          top: 0px;
          transform-origin: left center;
        }

        .hamburger span:nth-child(2) {
          top: 8px;
          transform-origin: left center;
        }

        .hamburger span:nth-child(3) {
          top: 16px;
          transform-origin: left center;
        }

        .hamburger.open span:nth-child(1) {
          transform: rotate(45deg);
          top: -1px;
          left: 4px;
        }

        .hamburger.open span:nth-child(2) {
          width: 0%;
          opacity: 0;
        }

        .hamburger.open span:nth-child(3) {
          transform: rotate(-45deg);
          top: 15px;
          left: 4px;
        }

        /* Animations */
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .nav-links {
            gap: 30px;
          }
          
          .desktop-cta {
            padding: 10px 24px;
            font-size: 0.9rem;
          }
        }

        @media (max-width: 768px) {
          .header {
            background: rgba(2, 6, 23, 0.95);
            backdrop-filter: blur(10px);
          }

          .contact-bar {
            display: none !important;
          }

          .container {
            padding: 0 20px;
          }

          .navbar {
            padding: 15px 0;
          }

          .nav-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .nav-links {
            position: fixed;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100vh;
            background: linear-gradient(135deg, #020617 0%, #0f172a 100%);
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 30px;
            transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            z-index: 1000;
            padding: 80px 20px;
            overflow-y: auto;
          }

          .nav-links.active {
            left: 0;
          }

          .nav-link {
            font-size: 1.3rem;
            padding: 15px 0;
            width: 200px;
            text-align: center;
            animation: fadeInUp 0.5s ease forwards;
            opacity: 0;
          }

          .nav-links.active .nav-link:nth-child(1) {
            animation-delay: 0.1s;
          }
          .nav-links.active .nav-link:nth-child(2) {
            animation-delay: 0.2s;
          }
          .nav-links.active .nav-link:nth-child(3) {
            animation-delay: 0.3s;
          }
          .nav-links.active .nav-link:nth-child(4) {
            animation-delay: 0.4s;
          }
          .nav-links.active .nav-link:nth-child(5) {
            animation-delay: 0.5s;
          }
          .nav-links.active .nav-link:nth-child(6) {
            animation-delay: 0.6s;
          }

          .desktop-cta {
            display: none;
          }

          .mobile-cta {
            display: block;
            margin-top: 30px;
            animation: fadeInUp 0.5s ease 0.7s forwards;
            opacity: 0;
          }

          .contact-cta {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 15px 30px;
            background: linear-gradient(135deg, #22d3ee, #3b82f6);
            color: #020617;
            border-radius: 12px;
            border: none;
            font-weight: 600;
            font-size: 1.1rem;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .contact-cta:hover {
            transform: scale(1.05);
          }

          .logo-primary {
            font-size: 1.4rem;
          }

          .logo-tagline {
            font-size: 0.7rem;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 16px;
          }

          .logo-img {
            width: 40px;
            height: 40px;
          }

          .nav-link {
            font-size: 1.2rem;
            width: 100%;
            max-width: 200px;
          }

          .contact-cta {
            padding: 12px 24px;
            font-size: 1rem;
          }
        }

        /* Tablet Landscape */
        @media (min-width: 769px) and (max-width: 1024px) and (orientation: landscape) {
          .nav-links {
            gap: 25px;
          }
          
          .nav-link {
            font-size: 0.95rem;
          }
          
          .desktop-cta {
            padding: 10px 20px;
            font-size: 0.9rem;
          }
        }

        /* Accessibility Improvements */
        .nav-link:focus-visible {
          outline: 2px solid #22d3ee;
          outline-offset: 4px;
          border-radius: 4px;
        }

        .desktop-cta:focus-visible {
          outline: 2px solid #22d3ee;
          outline-offset: 2px;
        }

        .nav-toggle:focus-visible {
          outline: 2px solid #22d3ee;
          outline-offset: 4px;
          border-radius: 4px;
        }

        /* Reduced Motion Support */
        @media (prefers-reduced-motion: reduce) {
          .header,
          .nav-link,
          .desktop-cta,
          .hamburger span,
          .nav-links,
          .mobile-cta {
            transition: none;
            animation: none;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;